import { type FormEvent, type MouseEvent as ReactMouseEvent } from 'react'
import {
  FolderOpen,
  Library,
  ListFilter,
  MoreVertical,
  Music,
  Pause,
  Pencil,
  Play,
  RefreshCw,
  Search,
  Trash2,
  TriangleAlert
} from 'lucide-react'
import { getAudioImageUrl, resolveMediaUrl, type AudioTrack } from '../../../api'
import { EmptyState, IconButton, MobileEntityList, MobileEntityRow, SearchField } from '../../../components/ui'
import styles from './AudioTrackMobileList.module.css'

type AudioTrackMobileListProps = {
  activeFilterCount: number
  actionMenuTrackId: string | null
  items: AudioTrack[]
  loading: boolean
  loadingTrackId: string | null
  onDelete: (track: AudioTrack) => Promise<void>
  onEdit: (track: AudioTrack) => void
  onManageGroups: (track: AudioTrack) => void
  onOpenFilters: () => void
  onPlayTrack: (track: AudioTrack) => void
  onReprocess: (track: AudioTrack) => Promise<void>
  onSearchInputChange: (value: string) => void
  onSearchSubmit: (event?: FormEvent<HTMLFormElement>) => void
  onToggleActionMenu: (event: ReactMouseEvent<HTMLButtonElement>, trackId: string) => void
  onCloseActionMenu: () => void
  playingTrackId: string | null
  searchInput: string
  emptyMessage: string
}

const audioTrackStatusLabels: Record<string, string> = {
  uploading: 'Загружается',
  queued: 'Ожидает обработки',
  processing: 'Обрабатывается',
  unsupported: 'Нет аудио',
  failed: 'Не удалось обработать',
  archived: 'В архиве'
}

const audioProcessingStepLabels: Record<NonNullable<AudioTrack['processing_summary']['current_step_type']>, string> = {
  inspect_audio: 'Inspecting audio',
  generate_audio_image_derivatives: 'Updating artwork',
  analyze_audio_features: 'Detecting BPM',
  analyze_audio_loudness: 'Analyzing loudness',
  analyze_audio_labels: 'Analyzing labels',
  generate_lossless_audio: 'Preparing FLAC'
}

const getProcessingLabel = (track: AudioTrack): string | null => {
  const summary = track.processing_summary

  if (summary.status === 'none' || summary.status === 'completed') {
    return null
  }

  const stepLabel = summary.current_step_type ? audioProcessingStepLabels[summary.current_step_type] : 'Processing'

  if (summary.status === 'failed') {
    return summary.current_step_error ? `${stepLabel} failed: ${summary.current_step_error}` : `${stepLabel} failed`
  }

  if (summary.current_step_status === 'queued') {
    return `${stepLabel} queued`
  }

  return stepLabel
}

export const AudioTrackMobileList = ({
  activeFilterCount,
  actionMenuTrackId,
  items,
  loading,
  loadingTrackId,
  onDelete,
  onEdit,
  onManageGroups,
  onOpenFilters,
  onPlayTrack,
  onReprocess,
  onSearchInputChange,
  onSearchSubmit,
  onToggleActionMenu,
  onCloseActionMenu,
  playingTrackId,
  searchInput,
  emptyMessage
}: AudioTrackMobileListProps) => (
  <MobileEntityList
    toolbar={(
      <form className={styles.toolbar} onSubmit={onSearchSubmit}>
        <SearchField
          className={styles.searchField ?? ''}
          onChange={(event) => onSearchInputChange(event.target.value)}
          placeholder="Search tracks..."
          size="mobile"
          value={searchInput}
        />
        <button
          aria-label="Search tracks"
          className={styles.iconButton}
          type="submit"
        >
          <Search size={17} />
        </button>
        <button
          aria-label={activeFilterCount > 0 ? `Open filters, ${activeFilterCount} active` : 'Open filters'}
          className={[styles.iconButton, activeFilterCount > 0 ? styles.iconButtonActive : ''].filter(Boolean).join(' ')}
          onClick={onOpenFilters}
          title="Filters"
          type="button"
        >
          <ListFilter size={17} />
          {activeFilterCount > 0 && <span className={styles.filterCount}>{activeFilterCount}</span>}
        </button>
      </form>
    )}
    empty={items.length === 0 ? <EmptyState message={loading ? 'Loading audio tracks...' : emptyMessage} /> : null}
  >
    {items.map((track) => {
      const isReady = track.status === 'ready'
      const statusLabel = audioTrackStatusLabels[track.status] ?? track.status
      const trackDisplayTitle = track.artist ? `${track.artist} - ${track.title}` : track.title
      const artworkUrl = getAudioImageUrl(track, 'thumb')
      const isLoadingTrack = loadingTrackId === track.id
      const processingLabel = getProcessingLabel(track)
      const linkedPromptTitle = track.suno_prompt_binding?.current_prompt_title ?? track.suno_prompt_binding?.prompt_snapshot.title
      const linkedPromptTitleChanged = !!(track.suno_prompt_binding && track.suno_prompt_binding.current_prompt_title && track.suno_prompt_binding.current_prompt_title !== track.suno_prompt_binding.prompt_snapshot.title)
      const hasReadyLossless = track.lossless_status === 'ready'
      const actionMenuOpen = actionMenuTrackId === track.id

      return (
        <MobileEntityRow
          key={track.id}
          className={styles.trackRow ?? ''}
          leading={isReady ? (
            <IconButton
              disabled={isLoadingTrack}
              icon={isLoadingTrack ? <span className="track-status-spinner" /> : playingTrackId === track.id ? <Pause size={15} /> : <Play size={15} />}
              iconSize="play"
              label={isLoadingTrack ? `Loading ${track.title}` : `Preview ${track.title}`}
              loading={isLoadingTrack}
              onClick={() => onPlayTrack(track)}
              title={isLoadingTrack ? 'Loading preview' : playingTrackId === track.id ? 'Pause' : 'Play preview'}
            />
          ) : (
            <span
              aria-label={statusLabel}
              className={`track-status-icon track-status-icon-${track.status}`}
              role="img"
              title={statusLabel}
            >
              {track.status === 'queued' || track.status === 'processing' || track.status === 'uploading' ? (
                <span className="track-status-spinner" />
              ) : (
                <TriangleAlert size={16} />
              )}
            </span>
          )}
          media={(
            <span className={styles.artwork}>
              {artworkUrl ? (
                <img alt="" src={resolveMediaUrl(artworkUrl)} />
              ) : (
                <Music size={15} />
              )}
            </span>
          )}
          title={(
            <span className={styles.title}>
              <span>{trackDisplayTitle}</span>
              {hasReadyLossless && (
                <span
                  aria-label="Ready FLAC source"
                  className={styles.hqMarker}
                  title="Ready FLAC source"
                >
                  HQ
                </span>
              )}
            </span>
          )}
          subtitle={(
            <>
              <span
                aria-label={linkedPromptTitle ? `Linked to: ${linkedPromptTitle}` : 'No Prompt Library link'}
                className={[
                  styles.promptMarker,
                  linkedPromptTitle ? styles.promptMarkerLinked : '',
                  linkedPromptTitleChanged ? styles.promptMarkerChanged : ''
                ].filter(Boolean).join(' ')}
                title={linkedPromptTitle ? `Linked to: ${linkedPromptTitle}${linkedPromptTitleChanged ? ' (title changed since binding)' : ''}` : 'No Prompt Library link'}
              >
                <Library size={12} aria-hidden="true" />
                <span>{linkedPromptTitle ?? '-'}</span>
              </span>
              {!isReady && (
                <span className={['track-status-text', `track-status-text-${track.status}`, styles.status].join(' ')}>
                  {statusLabel}
                </span>
              )}
              {processingLabel && (
                <span className={['track-processing-text', `track-processing-text-${track.processing_summary.status}`, styles.processing].join(' ')}>
                  {processingLabel}
                </span>
              )}
              <span className={styles.groups} aria-label={`Groups for ${track.title}`}>
                {track.groups.length > 0
                  ? track.groups.map((group) => group.name).join(', ')
                  : 'No groups'}
              </span>
            </>
          )}
          actions={(
            <div
              className={styles.actionMenuWrap}
              data-track-action-surface
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <IconButton
                active={actionMenuOpen}
                aria-expanded={actionMenuOpen}
                aria-haspopup="dialog"
                icon={<MoreVertical size={15} />}
                label={`Open actions for ${track.title}`}
                onClick={(event) => onToggleActionMenu(event, track.id)}
                title="Actions"
              />
              {actionMenuOpen && (
                <div
                  className={styles.actionSheetBackdrop}
                  onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                      onCloseActionMenu()
                    }
                  }}
                >
                  <section
                    aria-label={`Actions for ${track.title}`}
                    aria-modal="true"
                    className={styles.actionSheetPanel}
                    data-track-action-surface
                    onMouseDown={(event) => event.stopPropagation()}
                    role="dialog"
                  >
                    <div className={styles.actionSheetHeader}>
                      <span>Actions</span>
                      <strong>{trackDisplayTitle}</strong>
                    </div>
                    <div className={styles.actionSheetActions}>
                      <button
                        className={styles.actionMenuItem}
                        onClick={() => {
                          onCloseActionMenu()
                          onEdit(track)
                        }}
                        type="button"
                        aria-label={`Edit ${track.title}`}
                      >
                        <Pencil size={17} />
                        <span>Edit</span>
                      </button>
                      <button
                        className={styles.actionMenuItem}
                        onClick={() => {
                          onCloseActionMenu()
                          onManageGroups(track)
                        }}
                        type="button"
                        aria-label={`Manage groups for ${track.title}`}
                      >
                        <FolderOpen size={17} />
                        <span>Manage groups</span>
                      </button>
                      <button
                        className={styles.actionMenuItem}
                        onClick={() => {
                          onCloseActionMenu()
                          void onReprocess(track)
                        }}
                        type="button"
                        aria-label={`Reprocess ${track.title}`}
                      >
                        <RefreshCw size={17} />
                        <span>Reprocess</span>
                      </button>
                      <button
                        className={[styles.actionMenuItem, styles.actionDanger].filter(Boolean).join(' ')}
                        onClick={() => {
                          onCloseActionMenu()
                          void onDelete(track)
                        }}
                        type="button"
                        aria-label={`Remove ${track.title}`}
                      >
                        <Trash2 size={17} />
                        <span>Delete</span>
                      </button>
                    </div>
                    <button
                      className={styles.actionSheetCancel}
                      onClick={onCloseActionMenu}
                      type="button"
                    >
                      Cancel
                    </button>
                  </section>
                </div>
              )}
            </div>
          )}
        />
      )
    })}
  </MobileEntityList>
)
